// D-068: next to the shop name, the blue tick once Adyvance verified the business, or a grey
// "Unverified" badge until then. `verified` undefined (an older /me) shows nothing. The sidebar,
// which has no room for the word, uses the grey seal (`VerifiedTick unverified`) instead.
// The Unverified badge opens Store profile → Business details at the store verification section.
// Inside another control (the store switcher button) it can't be a nested link, so `inButton`
// makes it a click target that stops the button's own click; keyboard users reach the same page
// from the profile menu's badge, which is a real link.
import type { MouseEvent, ReactNode } from "react";
import { t } from "@ady/i18n";
import { VerifiedTick } from "@ady/ui";
import { AppLink, useGo } from "./AppLink";

export const VERIFY_ANCHOR = "store-verification";
export const verificationHref = (orgSlug: string, storeSlug: string) =>
  `/${orgSlug}/${storeSlug}/settings/store?tab=business#${VERIFY_ANCHOR}`;

const CHIP =
  "flex-none rounded-full bg-sunken px-1.5 py-px text-2xs font-semibold text-fg-tertiary no-underline hover:bg-hover hover:text-link";

export function VerifiedMark({
  verified,
  href,
  inButton = false,
  onGo,
}: {
  verified: boolean | undefined;
  /** Where the Unverified badge goes (verificationHref). */
  href?: string;
  inButton?: boolean;
  /** Called before navigating (e.g. to close a menu). */
  onGo?: () => void;
}) {
  if (verified) return <VerifiedTick label={t("shell.verified")} />;
  if (verified === undefined) return null;
  const label = t("shell.unverified");
  if (!href)
    return (
      <span className={CHIP} title={t("shell.unverifiedHint")} data-testid="unverified-chip">
        {label}
      </span>
    );
  if (inButton)
    return (
      <GoSpan href={href} onGo={onGo} className={CHIP}>
        {label}
      </GoSpan>
    );
  return (
    <AppLink
      href={href}
      onClick={() => onGo?.()}
      className={CHIP}
      title={t("shell.unverifiedHint")}
      data-testid="unverified-chip"
    >
      {label}
    </AppLink>
  );
}

/** A click target inside a button: navigates without triggering the button. */
export function GoSpan({
  href,
  onGo,
  className,
  children,
}: {
  href: string;
  onGo?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const go = useGo();
  const click = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onGo?.();
    go(href);
  };
  return (
    <span
      className={className ? `cursor-pointer ${className}` : "cursor-pointer"}
      title={t("shell.unverifiedHint")}
      onClick={click}
      data-testid="unverified-chip"
    >
      {children}
    </span>
  );
}
